import React, { useState, useEffect, useRef } from "react";

const MIN_AMOUNT = 0.0001;
const MAX_TOTAL_DIGITS = 15;
const MAX_DECIMAL_PLACES = 8;

// For USDT only ERC-20 and TRC-20 are supported in the checkout flow.
const TOKEN_OPTIONS = {
  USDT: [
    { value: "erc", label: "ERC-20" },
    { value: "trc", label: "TRC-20" },
  ],
};

// Human-readable label for a tokenType value
const TOKEN_LABEL = {
  erc: "ERC-20",
  trc: "TRC-20",
  bep: "BSC",
  pol: "POL",
  NATIVE: "NATIVE",
};

// Maps the parent's selectedNetwork string ("ERC", "TRC", …) to internal tokenType
const networkToTokenType = (network) => {
  const map = { ERC: "erc", TRC: "trc", BSC: "bep", POL: "pol", NATIVE: "NATIVE" };
  return map[(network || "").toUpperCase()] ?? null;
};

const getFixedTokenType = (code) => {
  if ((code || "").toUpperCase() === "USDC") return "erc";
  if ((code || "").toUpperCase() === "HCX") return "erc";
  return "NATIVE";
};

/**
 * PabtekPaymentPopup
 *
 * Matches the design system of CheckoutPaymentOptions (gradient header,
 * co-modal style) — no MUI dependency.
 *
 * Props:
 *   open            {boolean}
 *   onClose         {function}
 *   onProceed       {function({ amount, currencyId, currencyCode, usdEquivalent, tokenType })}
 *   currencyId      {number|string}
 *   currencyCode    {string}  e.g. "USDT"
 *   userUuid        {string}
 *   selectedNetwork {string}  pre-selected network from checkout ("ERC" | "TRC" | …)
 *                             When provided the selector is replaced by a read-only badge.
 *   requiredAmount  {number}  minimum crypto amount required to complete payment (from checkout)
 */
const PabtekPaymentPopup = ({
  open,
  onClose,
  onProceed,
  onPaymentLinkOpened,
  currencyId,
  currencyCode,
  userUuid = "",
  selectedNetwork = "",
  requiredAmount = 0,
  paymentIntentId = "",
  pollingError = "",
  directPollingMode = false,
}) => {
  // ── Amount input ─────────────────────────────────────────
  const [amount, setAmount] = useState("");
  const [amountError, setAmountError] = useState("");
  const [inputFocused, setInputFocused] = useState(false);

  // ── Market price ─────────────────────────────────────────
  const [unitRate, setUnitRate] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceError, setPriceError] = useState("");

  // ── Token type ───────────────────────────────────────────
  const tokenOptions =
    TOKEN_OPTIONS[(currencyCode || "").toUpperCase()] ?? null;
  const defaultTokenType = tokenOptions
    ? tokenOptions[0].value
    : getFixedTokenType(currencyCode);
  const [tokenType, setTokenType] = useState(defaultTokenType);

  // ── Payment link ─────────────────────────────────────────
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState("");

  // ── Polling state (after payment link opens) ──────────────
  const [pollingMode, setPollingMode] = useState(false);

  // ── Derived values ────────────────────────────────────────
  const effectiveMin =
    requiredAmount > 0
      ? Math.max(MIN_AMOUNT, requiredAmount)
      : MIN_AMOUNT;
  const parsedAmount = parseFloat(amount);
  const isAmountValid = !isNaN(parsedAmount) && parsedAmount >= effectiveMin;
  const usdEquivalent =
    isAmountValid && unitRate !== null ? parsedAmount * unitRate : null;
  const canProceed =
    isAmountValid &&
    !amountError &&
    unitRate !== null &&
    !priceLoading &&
    !paymentLoading;

  // ── Lock body scroll while open ───────────────────────────
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // ── Fetch market price on open ────────────────────────────
  useEffect(() => {
    if (!open) return;

    const initAmt = requiredAmount > 0 ? String(requiredAmount) : "";
    setAmount(initAmt);
    setAmountError("");
    setUnitRate(null);
    setPriceError("");
    setPaymentError("");
    setPollingMode(false);
    // Use the parent's already-selected network when provided, otherwise fall back to defaults
    const mapped = networkToTokenType(selectedNetwork);
    setTokenType(
      mapped
        ? mapped
        : TOKEN_OPTIONS[(currencyCode || "").toUpperCase()]
        ? TOKEN_OPTIONS[(currencyCode || "").toUpperCase()][0].value
        : getFixedTokenType(currencyCode)
    );

    if (!currencyCode) return;

    setPriceLoading(true);
    fetch("https://accounts.paybito.com/api/home/marketPrice", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        currency: currencyCode,
        baseCurrency: "USD",
        action: 1,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        const mp = data?.marketPrice;
        if (data?.error?.error_data === 0 && mp && mp > 0) {
          setUnitRate(mp);
        } else {
          setPriceError(
            data?.error?.error_msg || "Failed to fetch market price."
          );
        }
      })
      .catch(() => setPriceError("Failed to fetch market price."))
      .finally(() => setPriceLoading(false));
  }, [open, currencyCode]);

  // ── Validation ────────────────────────────────────────────
  // Returns true if the raw string satisfies the digit-count rules
  const isValidAmountFormat = (val) => {
    if (!val) return true;
    const parts = val.split(".");
    const intDigits = (parts[0] || "").length;
    const decDigits = (parts[1] || "").length;
    if (decDigits > MAX_DECIMAL_PLACES) return false;
    if (intDigits + decDigits > MAX_TOTAL_DIGITS) return false;
    return true;
  };

  const validateAmount = (val) => {
    const num = parseFloat(val);
    if (!val) {
      setAmountError("Amount is required.");
    } else if (isNaN(num) || num < effectiveMin) {
      setAmountError(
        `Amount must be at least ${effectiveMin} ${currencyCode || ""}.`
      );
    } else {
      const parts = val.split(".");
      const decDigits = (parts[1] || "").length;
      if (decDigits > MAX_DECIMAL_PLACES) {
        setAmountError(`Maximum ${MAX_DECIMAL_PLACES} decimal places allowed.`);
      } else if ((parts[0] || "").length + decDigits > MAX_TOTAL_DIGITS) {
        setAmountError(`Maximum ${MAX_TOTAL_DIGITS} digits allowed in total.`);
      } else {
        setAmountError("");
      }
    }
  };

  const handleAmountChange = (e) => {
    const val = e.target.value;
    setAmount(val);
    validateAmount(val);
  };

  // ── Generate payment link & open in new tab ───────────────
  const handleProceed = async () => {
    if (!canProceed) return;
    setPaymentLoading(true);
    setPaymentError("");

    try {
      const res = await fetch(
        "https://accounts.paybito.com/api/transaction/generatePabtekPaymentLink",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
          body: JSON.stringify({
            userUuid: userUuid || "",
            baseCurrencyId: "1",
            amount: String(
              unitRate != null && parsedAmount > 0
                ? parsedAmount * unitRate
                : 0
            ), // counterAmount * marketPrice = total USD value
            counterCurrencyId: String(currencyId ?? ""),
            counterAmount: String(parsedAmount),
            marketPrice: String(unitRate ?? ""),
            tokenType,
            paymentIntentId: paymentIntentId || "",
          }),
        }
      );

      const data = await res.json();

      if (data?.paymentLink) {
        window.open(data.paymentLink, "_blank", "noopener,noreferrer");
        setPollingMode(true);
        if (typeof onPaymentLinkOpened === "function") {
          onPaymentLinkOpened({
            amount: parsedAmount,
            currencyId,
            currencyCode,
            usdEquivalent,
            tokenType,
            marketPrice: unitRate,
            paymentLinkId: data.paymentLinkId || "",
          });
        }
        if (typeof onProceed === "function") {
          onProceed({
            amount: parsedAmount,
            currencyId,
            currencyCode,
            usdEquivalent,
            tokenType,
          });
        }
      } else {
        setPaymentError(
          data?.error?.error_msg ||
            "Failed to generate payment link. Please try again."
        );
      }
    } catch {
      setPaymentError(
        "Failed to generate payment link. Please try again."
      );
    } finally {
      setPaymentLoading(false);
    }
  };

  const logoUrl = `https://brokersexchange.s3.us-west-1.amazonaws.com/currency_logo/${(
    currencyCode || ""
  ).toUpperCase()}.png`;

  if (!open) return null;

  // ── Polling-mode screen ───────────────────────────────────
  if (pollingMode || directPollingMode) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(15,23,42,0.7)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          fontFamily: "'Roboto', sans-serif",
          padding: "16px",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 440,
            background: "#fff",
            borderRadius: 16,
            overflow: "hidden",
            boxShadow: "0 25px 60px rgba(0,0,0,0.28)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <div
            style={{
              background: "linear-gradient(135deg,#1e40af 0%,#3b82f6 100%)",
              padding: "24px 24px 20px",
              position: "relative",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 3,
                background: "linear-gradient(90deg,#2563EB,#1D4ED8,#15803D)",
              }}
            />
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: "rgba(255,255,255,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <i className="fa fa-credit-card" style={{ fontSize: 18, color: "#fff" }} />
              </div>
              <div>
                <div style={{ color: "#fff", fontWeight: 700, fontSize: 17, lineHeight: 1.2 }}>
                  Buy with Card
                </div>
                <div style={{ color: "rgba(255,255,255,0.65)", fontSize: 12, marginTop: 2 }}>
                  Secure card payment
                </div>
              </div>
            </div>
          </div>

          {/* Body */}
          <div
            style={{
              padding: "40px 32px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 20,
            }}
          >
            {/* Spinner */}
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "rgba(37,99,235,0.08)",
                border: "2px solid rgba(37,99,235,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i
                className="fa fa-spinner fa-spin"
                style={{ fontSize: 28, color: "#2563eb" }}
              />
            </div>

            <div>
              <div
                style={{
                  fontSize: 17,
                  fontWeight: 700,
                  color: "#111827",
                  marginBottom: 10,
                }}
              >
                Payment is in process
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "#6b7280",
                  lineHeight: 1.6,
                  maxWidth: 320,
                }}
              >
                Your payment is being processed in the external tab. Please complete
                the payment there.
              </div>
            </div>

            {/* Warning banner — hidden when an error has occurred */}
            {!pollingError && (
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  padding: "14px 16px",
                  borderRadius: 10,
                  background: "#fef3c7",
                  border: "1px solid #f59e0b",
                  textAlign: "left",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <i
                  className="fa fa-exclamation-triangle"
                  style={{ color: "#d97706", flexShrink: 0, marginTop: 2, fontSize: 14 }}
                />
                <span style={{ color: "#78350f", fontSize: 13, fontWeight: 600, lineHeight: 1.5 }}>
                  Do not close or reload this page while your payment is being confirmed.
                </span>
              </div>
            )}

            {/* Polling error */}
            {pollingError ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  padding: "14px 16px",
                  borderRadius: 10,
                  background: "#fee2e2",
                  border: "1px solid #f87171",
                  textAlign: "left",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <i
                  className="fa fa-times-circle"
                  style={{ color: "#dc2626", flexShrink: 0, marginTop: 2, fontSize: 14 }}
                />
                <span style={{ color: "#991b1b", fontSize: 13, fontWeight: 600, lineHeight: 1.5 }}>
                  {pollingError}
                </span>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                Checking payment status…
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(15,23,42,0.55)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        fontFamily: "'Roboto', sans-serif",
        padding: "16px",
        boxSizing: "border-box",
      }}
      onClick={onClose}
    >
      {/* Keyframe animations */}
      <style>{`
        @keyframes ppFadeInUp {
          from { opacity:0; transform:translateY(20px); }
          to   { opacity:1; transform:translateY(0);    }
        }
        .pp-close-btn:hover  { background:rgba(255,255,255,0.25) !important; }
        .pp-cancel-btn:hover { background:rgba(0,0,0,0.05) !important; color:#374151 !important; }
        .pp-net-btn:hover    { border-color:#2563eb !important; color:#2563eb !important; }
        .pp-proceed-btn:hover:not(:disabled) {
          background:linear-gradient(135deg,#1e40af 0%,#1d4ed8 100%) !important;
          box-shadow:0 6px 24px rgba(37,99,235,0.45) !important;
          transform:translateY(-1px);
        }
        .pp-input-wrap { background:#ffffff !important; border-color:inherit !important; }
        .pp-input      { background:#ffffff !important; color:#111827 !important; border:none !important; outline:none !important; box-shadow:none !important; }
        .pp-input::-webkit-inner-spin-button,
        .pp-input::-webkit-outer-spin-button { -webkit-appearance:none; margin:0; }
        .pp-input[type=number] { -moz-appearance:textfield; }
        .pp-input::placeholder { color:#9ca3af !important; }
      `}</style>

      <div
        style={{
          width: "100%",
          maxWidth: 440,
          background: "#fff",
          borderRadius: 16,
          overflow: "hidden",
          boxShadow: "0 25px 60px rgba(0,0,0,0.28)",
          display: "flex",
          flexDirection: "column",
          animation: "ppFadeInUp .22s ease",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Gradient header (matches CheckoutPaymentOptions login modal) ── */}
        <div
          style={{
            background: "linear-gradient(135deg,#1e40af 0%,#3b82f6 100%)",
            padding: "24px 24px 20px",
            position: "relative",
          }}
        >
          {/* Accent strip */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              background:
                "linear-gradient(90deg,#2563EB,#1D4ED8,#15803D)",
            }}
          />

          {/* Close button */}
          <button
            className="pp-close-btn"
            onClick={onClose}
            title="Close"
            style={{
              position: "absolute",
              top: 14,
              right: 14,
              background: "rgba(255,255,255,0.15)",
              border: "none",
              borderRadius: 8,
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "#fff",
              fontSize: 20,
              lineHeight: 1,
              transition: "background .18s",
            }}
          >
            &times;
          </button>

          {/* Icon + title row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 18,
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: "rgba(255,255,255,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <i
                className="fa fa-credit-card"
                style={{ fontSize: 18, color: "#fff" }}
              />
            </div>
            <div>
              <div
                style={{
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 17,
                  lineHeight: 1.2,
                  letterSpacing: "-0.2px",
                }}
              >
                Buy with Card
              </div>
              <div
                style={{
                  color: "rgba(255,255,255,0.65)",
                  fontSize: 12,
                  marginTop: 2,
                }}
              >
                Secure card payment
              </div>
            </div>
          </div>

          {/* Currency badge row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            {/* Currency logo */}
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                overflow: "hidden",
              }}
            >
              <img
                src={logoUrl}
                alt={currencyCode}
                style={{ width: 42, height: 42, objectFit: "contain" }}
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
              <span
                style={{
                  display: "none",
                  width: "100%",
                  height: "100%",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 16,
                }}
              >
                {(currencyCode || "?")[0]}
              </span>
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 16,
                  lineHeight: 1.2,
                }}
              >
                {currencyCode || "—"}
              </div>

              {priceLoading ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginTop: 3,
                  }}
                >
                  <i
                    className="fa fa-spinner fa-spin"
                    style={{
                      fontSize: 10,
                      color: "rgba(255,255,255,0.6)",
                    }}
                  />
                  <span
                    style={{
                      fontSize: 12,
                      color: "rgba(255,255,255,0.6)",
                    }}
                  >
                    Fetching price…
                  </span>
                </div>
              ) : priceError ? (
                <span style={{ fontSize: 12, color: "#fca5a5" }}>
                  Price unavailable
                </span>
              ) : unitRate !== null ? (
                <span
                  style={{
                    fontSize: 12,
                    color: "rgba(255,255,255,0.75)",
                  }}
                >
                  1 {currencyCode} ={" "}
                  $
                  {unitRate.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 8,
                  })}{" "}
                  USD
                </span>
              ) : null}
            </div>

            {/* Live Rate badge */}
            {unitRate !== null && !priceLoading && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  background: "rgba(74,222,128,0.2)",
                  border: "1px solid rgba(74,222,128,0.35)",
                  color: "#86efac",
                  borderRadius: 20,
                  padding: "3px 10px",
                  fontSize: 11,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                <i className="fa fa-line-chart" style={{ fontSize: 10 }} />
                Live Rate
              </span>
            )}
          </div>
        </div>

        {/* ── Content area ─────────────────────────────────────── */}
        <div style={{ padding: "24px 24px 0" }}>
          {/* Amount input */}
          <div style={{ marginBottom: 4 }}>
            {/* Label */}
            <label
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 600,
                color: "#374151",
                marginBottom: 6,
                letterSpacing: "0.01em",
              }}
            >
              Amount
            </label>

            {/* Input wrapper — card-form style */}
            <div
              className="pp-input-wrap"
              style={{
                display: "flex",
                alignItems: "center",
                borderRadius: 10,
                background: "#ffffff",
                border: inputFocused
                  ? "1.5px solid #2563eb"
                  : amountError
                  ? "1.5px solid #ef4444"
                  : "1.5px solid #d1d5db",
                transition: "border-color .15s",
                overflow: "hidden",
              }}
            >
              {/* Number input */}
              <input
                type="number"
                placeholder="Enter amount"
                value={amount}
                min={MIN_AMOUNT}
                step="any"
                onChange={handleAmountChange}
                onFocus={() => setInputFocused(true)}
                onBlur={() => setInputFocused(false)}
                className="pp-input"
                style={{
                  flex: 1,
                  background: "#ffffff",
                  padding: "13px 14px",
                  fontSize: 16,
                  fontWeight: 500,
                  color: "#111827",
                  fontFamily: "'Roboto', sans-serif",
                  minWidth: 0,
                }}
              />

              {/* Divider */}
              <div
                style={{
                  width: 1,
                  height: 28,
                  background: "#e5e7eb",
                  flexShrink: 0,
                }}
              />

              {/* Currency badge on the right */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "0 14px",
                  background: "#ffffff",
                  flexShrink: 0,
                }}
              >
                <img
                  src={logoUrl}
                  alt={currencyCode}
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    objectFit: "contain",
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: 14,
                    color: "#374151",
                    whiteSpace: "nowrap",
                  }}
                >
                  {currencyCode}
                </span>
              </div>
            </div>

            {/* Helper / error text */}
            {amountError ? (
              <p
                style={{
                  margin: "5px 2px 0",
                  fontSize: 12,
                  color: "#ef4444",
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <i className="fa fa-exclamation-circle" style={{ fontSize: 11 }} />
                {amountError}
              </p>
            ) : (
              <p
                style={{
                  margin: "5px 2px 0",
                  fontSize: 12,
                  color: "#6b7280",
                }}
              >
                Min:{" "}
                <strong style={{ color: "#111827", fontWeight: 600 }}>
                  {effectiveMin} {currencyCode || ""}
                </strong>
                {requiredAmount > 0 && (
                  <span style={{ color: "#6b7280" }}>
                    {" "}(required payment amount)
                  </span>
                )}
              </p>
            )}

            {/* Required payment amount banner */}
            {requiredAmount > 0 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "10px 14px",
                  marginTop: 10,
                  borderRadius: 10,
                  background: "rgba(16,185,129,0.07)",
                  border: "1px solid rgba(16,185,129,0.25)",
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#065f46",
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                  }}
                >
                  <i className="fa fa-info-circle" style={{ color: "#10b981" }} />
                  Required payment amount
                </span>
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#065f46",
                  }}
                >
                  {requiredAmount} {currencyCode || ""}
                </span>
              </div>
            )}
          </div>

          {/* USD equivalent preview */}
          {usdEquivalent !== null && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 16px",
                marginTop: 14,
                borderRadius: 10,
                background: "rgba(37,99,235,0.06)",
                border: "1px solid rgba(37,99,235,0.15)",
              }}
            >
              <span style={{ fontSize: 13, color: "#6b7280" }}>
                You Pay
              </span>
              <span
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#2563eb",
                }}
              >
                ≈ $
                {usdEquivalent.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 6,
                })}
              </span>
            </div>
          )}

          {/* Network section — read-only badge when network is pre-selected by parent;
               toggle buttons when the user still needs to choose (HCX without a pre-selection) */}
          {tokenOptions && (
            <div style={{ marginTop: 18 }}>
              <span
                style={{
                  display: "block",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#6b7280",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  marginBottom: 8,
                }}
              >
                Network
              </span>

              {selectedNetwork ? (
                /* ── Read-only badge: network already chosen in checkout ── */
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "9px 16px",
                    borderRadius: 10,
                    background: "rgba(37,99,235,0.08)",
                    border: "2px solid #2563eb",
                    color: "#2563eb",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  <i
                    className="fa fa-check-circle"
                    style={{ fontSize: 14, color: "#2563eb" }}
                  />
                  {TOKEN_LABEL[tokenType] || tokenType}
                </div>
              ) : (
                /* ── Selectable toggle (HCX or any currency without a pre-picked network) ── */
                <div style={{ display: "flex", gap: 8 }}>
                  {tokenOptions.map((opt) => {
                    const isSelected = tokenType === opt.value;
                    return (
                      <button
                        key={opt.value}
                        className="pp-net-btn"
                        onClick={() => setTokenType(opt.value)}
                        style={{
                          flex: 1,
                          padding: "9px 0",
                          border: isSelected
                            ? "2px solid #2563eb"
                            : "1.5px solid #e2e6ef",
                          borderRadius: 10,
                          background: isSelected
                            ? "rgba(37,99,235,0.08)"
                            : "#fff",
                          color: isSelected ? "#2563eb" : "#6b7280",
                          fontWeight: 700,
                          fontSize: 13,
                          cursor: "pointer",
                          transition: "all .15s ease",
                          fontFamily: "'Roboto', sans-serif",
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Price-unavailable warning */}
          {priceError && (
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                marginTop: 16,
                padding: "12px 14px",
                borderRadius: 10,
                background: "#fef3c7",
                border: "1px solid #f59e0b",
                fontSize: 13,
              }}
            >
              <i
                className="fa fa-exclamation-triangle"
                style={{ marginTop: 1, color: "#d97706", flexShrink: 0 }}
              />
              <span style={{ color: "#78350f", fontWeight: 500 }}>{priceError}</span>
            </div>
          )}

          {/* Payment-link error */}
          {paymentError && (
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                marginTop: 16,
                padding: "12px 14px",
                borderRadius: 10,
                background: "#fee2e2",
                border: "1px solid #f87171",
                fontSize: 13,
              }}
            >
              <i
                className="fa fa-times-circle"
                style={{ marginTop: 1, flexShrink: 0, color: "#dc2626" }}
              />
              <span style={{ color: "#991b1b", fontWeight: 500 }}>{paymentError}</span>
            </div>
          )}
        </div>

        {/* ── Actions ──────────────────────────────────────────── */}
        <div style={{ padding: "20px 24px 24px" }}>
          {/* Divider */}
          <div
            style={{
              borderTop: "1px solid #e5e7eb",
              marginBottom: 18,
            }}
          />

          {/* Proceed button */}
          <button
            className="pp-proceed-btn"
            onClick={handleProceed}
            disabled={!canProceed}
            style={{
              width: "100%",
              padding: "14px 20px",
              border: "none",
              borderRadius: 12,
              background: canProceed
                ? "linear-gradient(135deg,#1565c0 0%,#1976d2 100%)"
                : "#e5e7eb",
              color: canProceed ? "#fff" : "#9ca3af",
              fontWeight: 700,
              fontSize: 15,
              cursor: canProceed ? "pointer" : "not-allowed",
              boxShadow: canProceed
                ? "0 4px 16px rgba(25,118,210,0.35)"
                : "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              marginBottom: 10,
              fontFamily: "'Roboto', sans-serif",
              transition: "background .2s, box-shadow .2s, transform .15s",
            }}
          >
            {paymentLoading ? (
              <>
                <i className="fa fa-spinner fa-spin" style={{ fontSize: 14 }} />
                Generating Payment Link…
              </>
            ) : (
              <>
                Proceed to Payment
                <i className="fa fa-external-link" style={{ fontSize: 13 }} />
              </>
            )}
          </button>

          {/* Cancel button */}
          <button
            className="pp-cancel-btn"
            onClick={onClose}
            disabled={paymentLoading}
            style={{
              width: "100%",
              padding: "12px 20px",
              border: "none",
              borderRadius: 12,
              background: "transparent",
              color: "#6b7280",
              fontWeight: 600,
              fontSize: 14,
              cursor: paymentLoading ? "not-allowed" : "pointer",
              fontFamily: "'Roboto', sans-serif",
              transition: "background .15s, color .15s",
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default PabtekPaymentPopup;
